import {
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { Redirect } from "react-router";
import { AnimatePresence, motion } from "motion/react";
import { useLocSettings } from "../../hooks/SettingsHooks";
import { useSiteFeatures } from "../../hooks/useSiteFeatures";
import Page from "../../layout/Page";
import { Helmet } from "react-helmet";
import DocumentTitle from "../../functional-components/DocumentTitle";
import Herobox from "../../components/Herobox";
import ProductsPageAside from "../../components/ProductsPageAside";
import {
  decodeFilterQstring,
  encodeFilterQstring,
  filterProducts,
  SORT_BY,
  sortProducts
} from "../../utils/ProductsUtils";
import ProductGrid from "../../components/ProductGrid";
import OptionButton from "../../components/OptionButton";
import { useMediaQuery } from "react-responsive";
import useWindowSize from "../../hooks/useWindowSize";
import {
  useBjelinTranslations,
  useProductTranslations
} from "../../hooks/TranslationHooks";
import { cmsAssetUrl } from "../../utils/CmsUtils";
import PageNotFoundFragment from "../../fragments/PageNotFoundFragment";
import { useCurrentUser } from "../../functional-components/UserContext";
import { useNavigationContext } from "../../functional-components/NavigationContext";
import COLORS from "../../config/colors";
import Icon from "../../components/Icon";
import CockpitLayoutComponents from "../../components/CockpitLayoutComponents";
import BjelinLogo from "../../components/BjelinLogo";
import { useRootCategories, useUserProducts } from "../../hooks/ProductHooks";
import SimpleLoadingSpinner from "../../components/SimpleLoadingSpinner";
import { shopUrl } from "../../config/urls";
import { Link } from "react-router-dom";
import NextSlideButton from "../../components/SectionNavigation/NextSlideButton";
import useScrollThreshold from "../../hooks/useScrollThreshold";
import {
  CategoryNav,
  CategorySubTreeNav
} from "../../components/CategoryLinks";
import "./style.scss";
import {
  categoryIsListable,
  filterProductsByCategory,
  getProductCategoryPedigree,
  productCategoryTitle,
  productCategoryUrl,
  productCategoryUrls,
  useCategoryFromPath,
  visibleOnSites
} from "../../utils/ProductCategoryUtils";
import { usePathLang } from "../../hooks/LangHooks";
import useResizeObserver from "@react-hook/resize-observer";

const ProductsPageContent = ({ location, category, categoryNavIsNeeded }) => {
  const t = useBjelinTranslations();
  const tp = useProductTranslations();
  const settings = useLocSettings();
  const features = useSiteFeatures();
  const { b2b } = useCurrentUser();
  const params = Object.fromEntries(new URLSearchParams(location.search));
  const isSmall = useMediaQuery({ maxWidth: 575.98 });
  const isLarge = useMediaQuery({ minWidth: 1124 });
  const winDim = useWindowSize();
  const products = useUserProducts();
  const pathLang = usePathLang();
  const isScrolled = useScrollThreshold(10);

  const [filterExpanded, setFilterExpanded] = useState(false);
  const [sort, setSort] = useState(
    "sort" in params ? parseInt(params.sort) : SORT_BY.relevance
  );
  const [layout, setLayout] = useState(
    "layout" in params && !isSmall ? params.layout : b2b ? "list" : "card"
  );
  const [activeFilters, setActiveFilters] = useState(
    "filter" in params ? decodeFilterQstring(params.filter) : {}
  );
  const filterCol = useRef();
  const container = useRef();
  const [filterHeight, setFilterHeight] = useState(500);

  const filterFields = category?.available_filters || [];

  const topBarHeight = 64;
  const secondaryTopBarHeight = categoryNavIsNeeded ? 64 : 0;
  const totalTopBarHeight = topBarHeight + secondaryTopBarHeight;

  const colorFilterName = "Staining";
  const gradeFilterName = "Grading";

  const [filterStyle, setFilterStyle] = useState({
    top: "calc(var(--padding-top) + " + topBarHeight + "px)"
  });

  useEffect(() => {
    if (category?.key) {
      //Reset filters on category switch
      setActiveFilters({});

      if (sort === SORT_BY.grade && !filterFields.includes(gradeFilterName)) {
        setSort(SORT_BY.relevance);
      }
      if (sort === SORT_BY.color && !filterFields.includes(colorFilterName)) {
        setSort(SORT_BY.relevance);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category?.key]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sort = params.get("sort");
    const layout = params.get("layout");
    const filter = params.get("filter");
    if (sort) setSort(parseInt(sort));
    if (layout && !isSmall) setLayout(layout);
    if (filter) {
      setActiveFilters(decodeFilterQstring(filter));
    }
  }, [isSmall, location.search]);

  const getCatUrl = useCallback(
    () => "/" + pathLang + (productCategoryUrl(category, pathLang) || shopUrl),
    [pathLang, category]
  );

  const setFilter = (field, value, set) => {
    const fs = { ...activeFilters };
    if (typeof fs[field] === "undefined") fs[field] = [];
    if (set) {
      if (!fs[field].includes(value)) fs[field].push(value);
    } else {
      fs[field] = fs[field].filter(v => v !== value);
      if (fs[field].length === 0) delete fs[field];
    }
    setActiveFilters(fs);
    window.history.replaceState(
      null,
      title,
      getCatUrl() +
        "?" +
        new URLSearchParams({
          filter: encodeFilterQstring(fs),
          sort,
          layout
        })
    );
  };

  const resetFilters = () => {
    setActiveFilters({});
    window.history.replaceState(
      null,
      title,
      getCatUrl() +
        "?" +
        new URLSearchParams({
          filter: encodeFilterQstring({}),
          sort,
          layout
        })
    );
  };

  const changeSorting = value => {
    setSort(parseInt(value));
    window.history.replaceState(
      null,
      title,
      getCatUrl() +
        "?" +
        new URLSearchParams({
          filter: encodeFilterQstring(activeFilters),
          sort: value,
          layout
        })
    );
  };

  const changeLayout = value => {
    setLayout(value);
    window.history.replaceState(
      null,
      title,
      getCatUrl() +
        "?" +
        new URLSearchParams({
          filter: encodeFilterQstring(activeFilters),
          sort,
          layout: value
        })
    );
  };

  const title = category?._id ? productCategoryTitle(category, tp) : t("shop");
  const valueOrder = settings.specification_value_order;

  const filterWidth = 300;
  const isFiltered = Object.keys(activeFilters).length > 0;

  let productsInCategory = filterProductsByCategory(products, category, true);

  const totalProducts = productsInCategory.length;
  const filteredProducts = filterProducts(
    productsInCategory,
    activeFilters || {}
  );

  const offset = useRef(0);
  const scrollingUp = useRef(true);
  const lastScroll = useRef(window.scrollY);
  const handleScroll = useCallback(() => {
    if (container.current.classList.contains("small")) {
      return;
    }
    //scrolling down
    if (window.scrollY > lastScroll.current) {
      if (scrollingUp.current) {
        offset.current = filterCol.current.offsetTop;
        scrollingUp.current = false;
      }
      let top =
        window.innerHeight - filterCol.current.getBoundingClientRect().height;
      top = top > totalTopBarHeight ? totalTopBarHeight : top;
      setFilterStyle({
        top: top + "px",
        marginTop: offset.current + "px",
        alignSelf: null,
        marginBottom: null,
        bottom: null
      });
    } else {
      //scrolling up
      if (!scrollingUp.current) {
        offset.current =
          container.current.getBoundingClientRect().height -
          filterCol.current.offsetTop -
          filterCol.current.getBoundingClientRect().height;
        scrollingUp.current = true;
      }
      setFilterStyle({
        marginBottom: offset.current + "px",
        marginTop: null,
        alignSelf: "end",
        bottom:
          -filterCol.current.getBoundingClientRect().height +
          window.innerHeight -
          totalTopBarHeight +
          "px",
        top: null
      });
    }
    lastScroll.current = window.pageYOffset;
  }, [totalTopBarHeight]);

  useLayoutEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  const handleResize = useCallback(() => {
    if (container.current.classList.contains("small")) {
      return;
    }
    if (scrollingUp.current) {
      scrollingUp.current = false;
      offset.current = filterCol.current.offsetTop;
      let top =
        window.innerHeight - filterCol.current.getBoundingClientRect().height;
      top = top > totalTopBarHeight ? totalTopBarHeight : top;

      setFilterStyle({
        top: top + "px",
        marginTop: offset.current + "px",
        alignSelf: null,
        marginBottom: null,
        bottom: null
      });
    }
  }, [totalTopBarHeight]);

  useResizeObserver(filterCol, () => {
    handleResize();
  });
  const windowSize = useWindowSize();

  useEffect(() => {
    handleResize();
  }, [totalProducts, handleResize, windowSize]);

  useLayoutEffect(() => {
    setFilterHeight(filterCol.current ? filterCol.current.offsetHeight : 500);
  }, [totalProducts]);

  return (
    <div
      style={{
        position: "relative",
        minHeight: filterHeight
      }}
      className={["products-grid", isLarge ? "large" : "small"].join(" ")}
      ref={container}
    >
      <motion.div
        id="filterCol"
        ref={filterCol}
        style={
          !isLarge
            ? {
                top: topBarHeight + "px",
                ...(!isLarge
                  ? {
                      x: -filterWidth
                    }
                  : {}),
                width: filterWidth
              }
            : {
                ...filterStyle,
                width: filterWidth
              }
        }
        animate={{
          ...(!isLarge
            ? {
                x: filterExpanded ? 0 : -filterWidth
              }
            : {}),
          width: filterWidth
        }}
      >
        <ProductsPageAside
          category={category}
          products={filteredProducts}
          allProducts={productsInCategory}
          activeFilters={activeFilters}
          setFilter={setFilter}
          resetFilters={resetFilters}
        />
      </motion.div>
      {!isLarge && (
        <motion.div
          id="filterTab"
          style={{
            position: "fixed",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            zIndex: 100,
            top: `calc(var(--padding-top) + ${topBarHeight}px`,
            left: "env(safe-area-inset-left)",
            height: 64,
            paddingRight: 28,
            background: COLORS.bjelin_dark_grey,
            color: "#fff",
            lineHeight: "64px",
            fontSize: "36px",
            cursor: "pointer"
          }}
          animate={{
            x: filterExpanded ? filterWidth - 64 : 0
          }}
          transition={{ duration: 0.3 }}
          onClick={() => setFilterExpanded(!filterExpanded)}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: 64,
              flex: "0 0 64px",
              lineHeight: "64px",
              fontSize: "36px"
            }}
          >
            <Icon name={filterExpanded ? "xmark" : "sliders"} type="fal" />
          </div>
          <motion.div
            style={{
              height: 64,
              overflow: "hidden"
            }}
            animate={{
              width: isScrolled || filterExpanded ? 0 : "auto"
            }}
            transition={{ damping: 100 }}
          >
            <div
              style={{
                height: 64,
                width: "fit-content",
                padding: "0 28px 0 2px",
                lineHeight: "64px",
                fontSize: "0.6em"
              }}
            >
              {t("action.filter")}
            </div>
          </motion.div>
        </motion.div>
      )}
      <div
        id="productsCol"
        style={{
          ...(!isLarge
            ? {
                transform: `scale(${
                  filterExpanded
                    ? (winDim.innerWidth - filterWidth - 15) / winDim.innerWidth
                    : 1
                }) translateX(${
                  filterExpanded
                    ? (-8 * winDim.innerWidth) /
                      Math.max(winDim.innerWidth - filterWidth, 1)
                    : 0
                }px)`,
                marginTop: 0
              }
            : {}),
          padding: isSmall ? "0 15px" : "0 30px"
        }}
      >
        <>
          <motion.div
            id="productsHeader"
            style={{
              display: "flex",
              flexDirection: "column",
              rowGap: 4,
              height: isLarge ? 76 : 64,
              marginTop: 10,
              marginBottom: 20
            }}
          >
            <div
              style={{
                flex: isLarge ? "0 0 42px" : "0 0 30px",
                display: "flex",
                alignItems: "center"
              }}
            >
              {isFiltered ? (
                <span>
                  {t("product-grid.matches", {
                    count: filteredProducts.length,
                    total: totalProducts
                  })}
                </span>
              ) : (
                <span>
                  {tp("showing", {
                    entries: filteredProducts.length
                  })}
                </span>
              )}
            </div>
            <div
              style={{
                flex: "0 0 30px",
                display: "flex",
                alignItems: "center"
              }}
            >
              <strong className={"me-1"}>{t("sort.by.label")}:</strong>
              <select
                id={"sort-select"}
                onChange={e => changeSorting(e.currentTarget.value)}
                value={sort}
              >
                <option value={SORT_BY.relevance}>
                  {t("sort.by.relevance")}
                </option>
                {features.prices() && (
                  <>
                    <option value={SORT_BY.price_asc}>
                      {t("sort.by.price_asc")}
                    </option>
                    <option value={SORT_BY.price_desc}>
                      {t("sort.by.price_desc")}
                    </option>
                  </>
                )}
                <option value={SORT_BY.name}>{t("sort.by.name")}</option>
                {filterFields.includes(gradeFilterName) && (
                  <option value={SORT_BY.grade}>{t("sort.by.grade")}</option>
                )}
                {filterFields.includes(colorFilterName) && (
                  <option value={SORT_BY.color}>{t("sort.by.color")}</option>
                )}
              </select>
              {!isSmall && (
                <>
                  <strong className={"ms-3 me-1"}>
                    {t("display.as.label")}:
                  </strong>
                  <OptionButton
                    options={{
                      card: t("display.as.card"),
                      list: t("display.as.list")
                    }}
                    value={layout}
                    onChange={value => changeLayout(value)}
                  />
                </>
              )}
            </div>
          </motion.div>

          <AnimatePresence initial={false}>
            {filteredProducts.length ? (
              <ProductGrid
                key={category?.key}
                products={sortProducts(
                  filteredProducts,
                  parseInt(sort),
                  valueOrder
                )}
                preferredCategory={category}
                groupBy={parseInt(sort)}
                layout={layout}
                clickToFilter
              />
            ) : (
              <motion.div
                className="empty-msg"
                variants={{
                  hidden: { opacity: 0 },
                  visible: {
                    opacity: 1
                  }
                }}
                initial="hidden"
                animate="visible"
                exit="hidden"
              >
                <div>
                  <Icon name="face-sad-tear" size="4x" />
                </div>
                <div>{t("product-grid.no-match")}</div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      </div>
      {!isLarge && (
        <AnimatePresence>
          {filterExpanded && (
            <motion.div
              id="productGridCover"
              style={{
                position: "fixed",
                zIndex: 2,
                top: isLarge
                  ? 0
                  : "calc(var(--padding-top) + " + topBarHeight + "px)",
                bottom: 0,
                left: 0,
                right: 0,
                background: "rgba(0,0,0,0.5)"
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setFilterExpanded(false)}
            />
          )}
        </AnimatePresence>
      )}
    </div>
  );
};

const ProductsPage = ({ history, location, match }) => {
  const t = useBjelinTranslations();
  const tp = useProductTranslations();
  const features = useSiteFeatures();
  const catTree = useRootCategories();
  const products = useUserProducts();

  const heroBox = useRef();
  const wrapperRef = useRef();
  const heroBoxHeight = heroBox.current?.offsetHeight || 200;
  const hideScrollButton = useScrollThreshold(heroBoxHeight / 3);
  const isLarge = useMediaQuery({ minWidth: 1124 });

  const catPath = match.params.category;

  //can be undefined
  const _category = useCategoryFromPath(catPath);
  //never undefined
  const category = useMemo(() => _category || {}, [_category]);
  const categoryPath = useMemo(
    () => productCategoryUrl(_category),
    [_category]
  );
  const productCategoryPedigree = getProductCategoryPedigree(category);
  const { registerCurrentPaths, setAvailableOnSites } = useNavigationContext();
  useEffect(() => {
    registerCurrentPaths(productCategoryUrls(_category));
    setAvailableOnSites(visibleOnSites(_category));
  }, [registerCurrentPaths, setAvailableOnSites, _category]);

  const title = productCategoryTitle(category, tp);

  if (categoryPath && `${shopUrl}/${catPath}` !== categoryPath) {
    return <Redirect to={categoryPath} />;
  }

  const heroBoxBkgPath =
    category.shop_herobox_background_image &&
    category.shop_herobox_background_image.path;
  const description = t("category.meta-description", { category: title });

  let categoryLogo;
  let heroBoxShadow;
  if (catPath === "outlet") {
    categoryLogo = <BjelinLogo variant={"outlet"} />;
    heroBoxShadow = false;
  } else if (catPath === "concept") {
    categoryLogo = <BjelinLogo variant={"concept"} darkBG={true} />;
    heroBoxShadow = true;
  } else {
    categoryLogo = title;
    heroBoxShadow = true;
  }

  const breadcrumbItems = [
    features.shop()
      ? { url: shopUrl, text: t("shop") }
      : { url: shopUrl, text: t("menu-label.products") }
  ];
  productCategoryPedigree.forEach(cat => {
    breadcrumbItems.push({
      url: productCategoryUrl(cat),
      text: productCategoryTitle(cat, tp)
    });
  });

  const heroboxContent =
    category.page_content?.[0]?.component === "content_section"
      ? category.page_content?.[0]?.children
      : category.page_content;

  const categoryNavIsNeeded =
    catTree.filter(category => categoryIsListable(category, products)).length >
    1;

  return !_category ? (
    <Page title={""} name={"404"} key={"products-page-404"} transition="simple">
      <PageNotFoundFragment />
    </Page>
  ) : (
    <>
      <DocumentTitle title={title + " - " + t("shop")} />
      <Helmet>
        <meta property="og:site_name" content={"Bjelin"} />
        <meta property="og:type" content={"website"} />
        <meta property="og:title" content={title + " - " + t("shop")} />
        <meta
          property="og:description"
          name="description"
          content={description}
        />
        <meta property="og:image" content={heroBoxBkgPath} />
        {/*<meta property="fb:app_id" content={} />*/}
      </Helmet>

      <Page
        name="products-page"
        key="products-page"
        title={title + " - " + t("shop")}
        scrollToTop="no"
      >
        {isLarge && categoryNavIsNeeded && (
          <CategoryNav curCat={category} categories={catTree} />
        )}
        <Herobox
          ref={heroBox}
          textShadow={heroBoxShadow}
          className={"text-center"}
          backgroundImage={
            heroBoxBkgPath != null ? cmsAssetUrl(heroBoxBkgPath) : null
          }
        >
          <h1 style={{ marginTop: "1em" }}>{categoryLogo}</h1>
          {heroboxContent && (
            <CockpitLayoutComponents
              components={heroboxContent}
              style={{ marginBottom: "2rem" }}
            />
          )}
          {category.children.length > 0 && (
            <CategorySubTreeNav category={category} />
          )}
          {category.parent && (
            <div
              style={{
                position: "absolute",
                top: categoryNavIsNeeded
                  ? isLarge
                    ? "80px"
                    : "144px"
                  : "16px",
                left: "32px"
              }}
            >
              <Link
                to={productCategoryUrl(category.parent)}
                style={{
                  lineHeight: "3em",
                  fontWeight: 600,
                  fontSize: "1.2em"
                }}
              >
                <Icon name="arrow-left" />{" "}
                {productCategoryTitle(category.parent, tp)}
              </Link>
            </div>
          )}
          <NextSlideButton
            onClick={() => {
              window.scrollTo({
                top: heroBoxHeight - 128,
                behavior: "smooth"
              });
            }}
            slideDirection={"vertical"}
            direction={1}
            hidden={hideScrollButton}
            style={{ position: "absolute", bottom: "-2rem" }}
          />
        </Herobox>

        <div className="content-wrapper" ref={wrapperRef}>
          <Suspense
            fallback={
              <div
                className="center-content"
                style={{ height: "100vh", paddingBottom: "25vh" }}
              >
                <SimpleLoadingSpinner />
                <div>{tp("loading_products")}</div>
              </div>
            }
          >
            <ProductsPageContent
              history={history}
              location={location}
              match={match}
              category={_category}
              categoryNavIsNeeded={categoryNavIsNeeded}
            />
          </Suspense>
        </div>
      </Page>
    </>
  );
};

export default ProductsPage;
